/*
 * Frame pages: keeps the builder in a <template> until the shopper opens it,
 * then loads its stylesheet, mounts it and runs its scripts. Works for the
 * native builder (frame-builder.js) and, while installed, the CPB app block.
 */
(() => {
  const template = document.querySelector('template[data-gp-builder-template]');
  if (!template) return;
  const host = template.parentElement;
  let loading;
  let loaded = false;
  let failed = false;
  let pending = false; // still loading after a short wait: show "Loading…" with Cancel
  const status = document.createElement('div');
  status.className = 'gp-builder-loading';
  status.hidden = true;
  status.style.cssText = 'padding:48px 16px;text-align:center;';
  const message = document.createElement('p');
  message.setAttribute('role', 'status');
  const retry = document.createElement('button');
  retry.type = 'button';
  retry.className = 'button';
  retry.textContent = 'Retry loading customiser';
  retry.hidden = true;
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'button';
  cancel.textContent = 'Cancel';
  status.append(message, retry, cancel);
  // Shown only while a slow load is still running, or after a failure.
  function showStatus(text, canRetry) {
    if (!status.isConnected) host.append(status);
    message.textContent = text;
    retry.hidden = !canRetry;
    status.hidden = false;
    status.style.display = 'block';
  }
  cancel.addEventListener('click', () => {
    document.querySelector('[data-frame-open-builder]')?.focus();
    document.dispatchEvent(new CustomEvent('gp:builder-cancel'));
  });
  document.addEventListener('gp:frame-builder-visibility', event => {
    const visible = (failed || pending) && event.detail.visible;
    status.hidden = !visible;
    status.style.display = visible ? 'block' : 'none';
  });
  // A request that never answers would leave the shopper with an empty page.
  const withTimeout = (promise, ms, text) => Promise.race([
    promise,
    new Promise((resolve, reject) => setTimeout(() => reject(new Error(text)), ms))
  ]);
  function runScript({ source, anchor }) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      for (const attr of source.attributes) script.setAttribute(attr.name, attr.value);
      if (source.src) {
        script.async = false;
        script.onload = resolve;
        script.onerror = () => reject(new Error('Customiser script could not load.'));
      } else script.textContent = source.textContent;
      anchor.replaceWith(script);
      if (!script.src) resolve();
    });
  }
  let stylesReady;
  function loadStyles() {
    if (!template.dataset.gpBuilderStyles) return Promise.resolve();
    if (stylesReady) return stylesReady;
    stylesReady = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = template.dataset.gpBuilderStyles;
      link.onload = resolve;
      link.onerror = () => reject(new Error('Customiser styles could not load.'));
      document.head.append(link);
    });
    return stylesReady;
  }
  function mount() {
    const fragment = template.content.cloneNode(true);
    const scripts = [...fragment.querySelectorAll('script')].map(source => {
      const anchor = document.createComment('Deferred CPB script');
      source.replaceWith(anchor);
      return { source, anchor };
    });
    // The app's legacy personalisation bootstrap owns this local target instead
    // of replacing the native product form used for basic frame purchasing.
    if (template.hasAttribute('data-gp-builder-native')) {
      host.append(fragment);
      return scripts;
    }
    const legacyTarget = document.createElement('div');
    legacyTarget.className = 'cpb-payment-buttons';
    legacyTarget.hidden = true;
    fragment.prepend(legacyTarget);
    host.append(fragment);
    document.dispatchEvent(new CustomEvent("gp:builder-mounted"));
    return scripts;
  }
  function load() {
    if (loaded) return Promise.resolve();
    if (loading) return loading;
    // Holds the builder's space while it loads so the sections below don't slide up into view.
    document.documentElement.classList.add('gp-builder-loading');
    status.hidden = true;
    failed = false;
    retry.hidden = true;
    // Fetch the builder's scripts while its stylesheet loads.
    template.content.querySelectorAll('script[src]').forEach((source) => {
      if (document.querySelector(`link[rel="preload"][href="${source.getAttribute('src')}"]`)) return;
      const hint = document.createElement('link');
      hint.rel = 'preload';
      hint.as = 'script';
      hint.href = source.getAttribute('src');
      document.head.append(hint);
    });
    // (CPB shows its own loading screen.)
    const slow = setTimeout(() => {
      if (loaded || failed || !template.hasAttribute('data-gp-builder-native')) return;
      pending = true;
      showStatus('Loading your builder…', false);
    }, 800);
    loading = (async () => {
      await withTimeout(loadStyles(), 20000, 'Customiser styles took too long to load.');
      const scripts = mount();
      // CPB signals CPB_ON_PRODUCT_MOUNTED; the native builder (frame-builder.js)
      // signals gp:native-builder-mounted. Either one completes the load.
      const readyEvents = ['CPB_ON_PRODUCT_MOUNTED', 'gp:native-builder-mounted'];
      let didMount = false;
      const mounted = new Promise((resolve, reject) => {
        const stop = () => readyEvents.forEach((name) => document.body.removeEventListener(name, finish));
        const finish = () => { didMount = true; clearTimeout(timer); stop(); resolve(); };
        const timer = setTimeout(() => { stop(); reject(new Error('Customiser took too long to respond.')); }, 45000);
        readyEvents.forEach((name) => document.body.addEventListener(name, finish));
      });
      // Attach a handler immediately while external scripts load.
      mounted.catch(() => {});
      for (const script of scripts) await withTimeout(runScript(script), 20000, 'Customiser script took too long to load.');
      // The native builder mounts while its script runs: if it hasn't signalled
      // by now it failed to start, so offer the retry straight away.
      if (template.hasAttribute('data-gp-builder-native') && !didMount) throw new Error('Customiser failed to start.');
      host.querySelector('.cpb-payment-buttons')?.remove();
      await mounted;
      loaded = true;
      pending = false;
      clearTimeout(slow);
      status.remove();
      document.documentElement.classList.remove('gp-builder-loading');
    })().catch(error => {
      document.documentElement.classList.remove('gp-builder-loading');
      failed = true;
      pending = false;
      clearTimeout(slow);
      showStatus('The customiser could not load. You can retry or return to the product.', true);
      console.warn('[Growplay customiser]', error.message);
      throw error;
    });
    return loading;
  }
  retry.addEventListener('click', () => {
    // Reloading a failed third-party app in place can duplicate its listeners.
    // A customization-intent URL safely restarts the page and retries once.
    const url = new URL(location.href);
    url.searchParams.set('customize', 'true');
    location.assign(url.href);
  });
  // Most frame-page visitors open the builder, so fetch its code into the cache
  // once the page is idle; opening then doesn't wait on the network.
  const prefetch = () => {
    if (loading || navigator.connection?.saveData) return;
    const urls = [template.dataset.gpBuilderStyles, ...[...template.content.querySelectorAll('script[src]')].map(s => s.getAttribute('src'))];
    urls.filter(Boolean).forEach(href => {
      const hint = document.createElement('link');
      hint.rel = 'prefetch';
      hint.href = href;
      document.head.append(hint);
    });
  };
  const whenIdle = () => (window.requestIdleCallback ? requestIdleCallback(prefetch, { timeout: 4000 }) : setTimeout(prefetch, 2000));
  if (document.readyState === 'complete') whenIdle(); else window.addEventListener('load', whenIdle, { once: true });
  window.GrowplayBuilderLoader = { load };
  function openSavedSetup() {
    const params = new URLSearchParams(location.search);
    if (!params.has('configid') && !params.has('configId') && params.get('customize') !== 'true') return;
    const button = document.querySelector('[data-frame-open-builder]');
    if (!button) { load().catch(() => {}); return; }
    if (button.dataset.gpRevealBound === 'true') { button.click(); return; }
    // The controller may bind after this deferred loader runs.
    const observer = new MutationObserver(() => {
      if (button.dataset.gpRevealBound !== 'true') return;
      clearTimeout(timer);
      observer.disconnect();
      button.click();
    });
    observer.observe(button, { attributes: true, attributeFilter: ['data-gp-reveal-bound'] });
    const timer = setTimeout(() => observer.disconnect(), 10000);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', openSavedSetup, {once: true});
  else openSavedSetup();
})();
